import { useEffect, useCallback, useRef } from 'react';
import { useCanvas } from '../contexts/CanvasContext';
import { useDesignerStore } from '../stores';

// Import fabric for side effects
import 'fabric';

const AUTOSAVE_KEY_PREFIX = 'mm_design_autosave_';
const AUTOSAVE_DEBOUNCE_MS = 2000;

interface AutoSaveData {
  designData: object;
  productId: string;
  variantId?: string | null;
  savedAt: string;
}

export const useAutoSave = () => {
  const { canvas } = useCanvas();
  const { productId, variantId, setStatus } = useDesignerStore();
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSaveRef = useRef<string>('');

  const getStorageKey = useCallback(() => {
    if (!productId) return null;
    // Key by PRODUCT only (not variant) so a customer's uploaded art/text
    // survives switching size/color/print variants. Previously each variant
    // had its own key, so the design appeared to vanish when switching — the
    // server variant bar does a full page reload (src/routes/design.ts).
    return `${AUTOSAVE_KEY_PREFIX}${productId}`;
  }, [productId]);

  // Save to localStorage
  const saveToLocalStorage = useCallback(() => {
    if (!canvas || !productId) return;

    const storageKey = getStorageKey();
    if (!storageKey) return;

    try {
      const designData = canvas.toJSON([
        'id',
        'isGuide',
        'isMockup',
        'isBaseImage',
        'isMaskOverlay',
        'uploadedUrl',
        'serverUrl',
      ]);

      const dataString = JSON.stringify(designData);

      // Don't save if nothing changed
      if (dataString === lastSaveRef.current) return;

      const autoSaveData: AutoSaveData = {
        designData,
        productId,
        variantId,
        savedAt: new Date().toISOString(),
      };

      localStorage.setItem(storageKey, JSON.stringify(autoSaveData));
      lastSaveRef.current = dataString;
      setStatus('Auto-saved');

      // Clear status after a moment
      setTimeout(() => setStatus(''), 2000);
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  }, [canvas, productId, variantId, getStorageKey, setStatus]);

  // Debounced save
  const debouncedSave = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      saveToLocalStorage();
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [saveToLocalStorage]);

  // Load from localStorage
  const loadFromLocalStorage = useCallback((): boolean => {
    if (!canvas || !productId) return false;

    const storageKey = getStorageKey();
    if (!storageKey) return false;

    try {
      const savedData = localStorage.getItem(storageKey);
      if (!savedData) return false;

      const autoSaveData: AutoSaveData = JSON.parse(savedData);

      // Verify it's for the same product
      if (autoSaveData.productId !== productId) return false;

      // Restore ONLY the customer's objects (uploaded art / text) on top of the
      // variant mockup that the template loader already placed. We must NOT
      // loadFromJSON the whole saved scene — that would replace the CURRENT
      // variant's base image/mask with the saved variant's, showing the wrong
      // product. Mirrors VariantSelector's enliven approach.
      const savedObjects = ((autoSaveData.designData as any)?.objects || []).filter(
        (o: any) => !o.isMockup && !o.isBaseImage && !o.isGuide && !o.isMaskOverlay
      );
      if (savedObjects.length === 0) return false;

      const f = (window as any).fabric;
      if (!f?.util?.enlivenObjects) return false;

      f.util.enlivenObjects(
        savedObjects,
        (objects: any[]) => {
          objects.forEach((obj) => canvas.add(obj));
          // Keep any mask overlays above the restored art
          canvas.getObjects().forEach((obj) => {
            if (obj.isMaskOverlay) canvas.bringToFront(obj);
          });
          canvas.renderAll();
          lastSaveRef.current = JSON.stringify(autoSaveData.designData);
          setStatus('Restored your design');
          setTimeout(() => setStatus(''), 2000);
        },
        ''
      );

      return true;
    } catch (err) {
      return false;
    }
  }, [canvas, productId, getStorageKey, setStatus]);

  // Check if auto-save exists
  const hasAutoSave = useCallback((): boolean => {
    const storageKey = getStorageKey();
    if (!storageKey) return false;

    try {
      const savedData = localStorage.getItem(storageKey);
      if (!savedData) return false;

      const autoSaveData: AutoSaveData = JSON.parse(savedData);
      return autoSaveData.productId === productId;
    } catch {
      return false;
    }
  }, [productId, getStorageKey]);

  // Get auto-save info
  const getAutoSaveInfo = useCallback((): { savedAt: string } | null => {
    const storageKey = getStorageKey();
    if (!storageKey) return null;

    try {
      const savedData = localStorage.getItem(storageKey);
      if (!savedData) return null;

      const autoSaveData: AutoSaveData = JSON.parse(savedData);
      return { savedAt: autoSaveData.savedAt };
    } catch {
      return null;
    }
  }, [getStorageKey]);

  // Clear auto-save
  const clearAutoSave = useCallback(() => {
    const storageKey = getStorageKey();
    if (!storageKey) return;

    localStorage.removeItem(storageKey);
    lastSaveRef.current = '';
  }, [getStorageKey]);

  // Set up auto-save on canvas changes
  useEffect(() => {
    if (!canvas) return;

    const handleChange = () => {
      debouncedSave();
    };

    canvas.on('object:modified', handleChange);
    canvas.on('object:added', handleChange);
    canvas.on('object:removed', handleChange);

    return () => {
      canvas.off('object:modified', handleChange);
      canvas.off('object:added', handleChange);
      canvas.off('object:removed', handleChange);

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [canvas, debouncedSave]);

  // Persist immediately before the page unloads. The server variant bar
  // (src/routes/design.ts) does a full page reload on switch, so the 2s
  // debounced save may not have fired — capture the design synchronously here.
  useEffect(() => {
    const handlePageHide = () => saveToLocalStorage();
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [saveToLocalStorage]);

  return {
    saveToLocalStorage,
    loadFromLocalStorage,
    hasAutoSave,
    getAutoSaveInfo,
    clearAutoSave,
  };
};

export default useAutoSave;
